import { useEffect, useRef, useState } from 'react';

export const useIsFirstRender = () => {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = false;
  });
  return ref.current;
};

export const useOnFirstRender = (fn, cleanup) => {
  const isFirst = useIsFirstRender();

  useEffect(() => {
    if (isFirst) {
      fn();
    }
    return cleanup;
  });
};

export const useSkipOnFirstRender = (fn, deps) => {
  const isFirst = useIsFirstRender();

  useEffect(() => {
    if (isFirst) return;
    fn();
  }, deps);
};

export const useRenderCount = () => {
  const ref = useRef(0);
  useEffect(() => {
    ref.current += 1;
  });
  return ref.current;
};

export const useDebounce = (func, timeout) => {
  const ref = useRef();
  return (...args) => {
    clearTimeout(ref.current);
    ref.current = setTimeout(() => {
      func(...args);
    }, timeout);
  };
};

export const useStateRef = (init) => {
  const ref = useRef(init);
  const [_draw, setDraw] = useState(0);

  return [
    ref.current,
    (value) => {
      setDraw((d) => d + 1);
      ref.current = value;
    },
    () => ref.current,
  ];
};

export const useWait = () => {
  const [waiting, setWaiting] = useState(false);
  const [waitFor, _setWaitFor] = useState(null);

  const setWaitFor = (check, then) => {
    setWaiting(true);
    _setWaitFor({
      check,
      then: () => {
        setWaiting(false);
        then();
      },
    });
  };

  useEffect(() => {
    if (waiting && waitFor) {
      if (waitFor.check()) {
        waitFor.then();
      }
    }
  });

  return [waiting, setWaitFor];
};
